import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const FIELDS = [
  { key: "send_primary_need", label: "Primary SEND / support need", input: true },
  { key: "send_additional_needs", label: "Additional support needs", input: true },
  { key: "communication_needs", label: "Communication needs" },
  { key: "mobility_requirements", label: "Mobility requirements" },
  { key: "sensory_considerations", label: "Sensory considerations" },
  { key: "behavioural_considerations", label: "Behavioural considerations relevant to safe travel" },
  { key: "learning_considerations", label: "Learning / understanding considerations" },
  { key: "known_triggers", label: "Known triggers relevant to travel", operational: true },
  { key: "strategies_help", label: "Strategies that help the child", operational: true },
  { key: "reasonable_adjustments", label: "Required reasonable adjustments" },
  { key: "send_other", label: "Other relevant information" },
  { key: "additional_info_safe_travel", label: "Additional information relevant to safe travel", operational: true },
];

export default function SendTab({ child, reload }) {
  const { hasPermission } = useAuth();
  const isAdmin = hasPermission("children:update");
  const isOperational = !isAdmin && hasPermission("children:update_operational");
  const [form, setForm] = useState(() =>
    Object.fromEntries(FIELDS.map((f) => [f.key, child[f.key] || ""]))
  );
  const [saving, setSaving] = useState(false);

  const canEdit = (f) => isAdmin || (isOperational && f.operational);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach((f) => {
        if (canEdit(f)) payload[f.key] = form[f.key] || null;
      });
      await api.patch(`/children/${child.id}`, payload);
      toast.success("SEND & support information updated");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="send-tab">
      <CardHeader>
        <CardTitle className="text-lg">SEND &amp; support information</CardTitle>
        <p className="text-xs text-slate-500">
          Data minimisation: only information relevant to safe independent travel training is recorded.
          {isOperational && " As the assigned trainer you can update triggers, strategies and safe-travel notes; other fields are managed by administrators."}
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={save} className="space-y-4" data-testid="send-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label>{f.label}</Label>
                {f.input ? (
                  <Input data-testid={`send-${f.key}`} value={form[f.key]} disabled={!canEdit(f)}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                ) : (
                  <Textarea data-testid={`send-${f.key}`} rows={2} value={form[f.key]} disabled={!canEdit(f)}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                )}
              </div>
            ))}
          </div>
          {(isAdmin || isOperational) && (
            <Button data-testid="send-save-button" type="submit" disabled={saving}
              className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save SEND information"}
            </Button>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
